import type { ReactNode } from 'react';
import { GlassButton, StatusPill, useI18n } from '@servicesai/ui';

/** Official NiCE CXone artwork from the NiCE press-kit master SVG package. */
export function CxoneLogo({ className = 'h-6 w-auto' }: { className?: string }) {
  return (
    <img
      src="/nice-cxone.svg"
      alt="NiCE CXone"
      className={className}
      draggable={false}
    />
  );
}

export function CxoneBadge({
  status,
  label,
  className = '',
}: {
  status?: string | null;
  label?: ReactNode;
  className?: string;
}) {
  const healthy = status === 'live' || status === 'assigned' || status === 'active';
  const degraded = status === 'degraded' || status === 'failed' || status === 'disconnected';
  return (
    <span
      className={`inline-flex min-h-7 items-center gap-2 rounded-lg border border-seablue-100 bg-white px-2 py-1 shadow-sm ${className}`}
    >
      <CxoneLogo className="h-3.5 w-auto" />
      {label}
      {status && (
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            healthy ? 'bg-approve' : degraded ? 'bg-reject' : 'bg-review'
          }`}
          title={status}
        />
      )}
    </span>
  );
}

export function CxoneCrmBanner({
  conversationCount,
  onOpenQueue,
}: {
  conversationCount: number;
  onOpenQueue: () => void;
}) {
  const { l } = useI18n();
  return (
    <section className="relative overflow-hidden rounded-xl border border-seablue-100 bg-[linear-gradient(105deg,#ffffff_0%,#f2f8ff_58%,#e4f2ff_100%)] p-4 shadow-sm">
      <div
        aria-hidden
        className="absolute -end-12 -top-16 h-40 w-40 rounded-full bg-seablue-100/60 blur-2xl"
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="grid h-14 w-48 shrink-0 place-items-center rounded-xl border border-white bg-white px-4 shadow-sm">
            <CxoneLogo className="h-6 w-full object-contain" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-extrabold">
                {l({ en: 'Contact-center integration', ar: 'تكامل مركز الاتصال' })}
              </h2>
              <StatusPill
                state="published"
                label={l({ en: 'Synchronized', ar: 'متزامن' })}
              />
            </div>
            <p className="mt-1 text-xs leading-relaxed text-ink-soft">
              {l({
                en: `${conversationCount} customer conversations are linked to NiCE CXone routing, queue, and agent ownership data.`,
                ar: `${conversationCount} محادثة متعامل مرتبطة ببيانات التوجيه والقائمة وملكية الموظف في NiCE CXone.`,
              })}
            </p>
          </div>
        </div>
        <GlassButton variant="primary" onClick={onOpenQueue}>
          {l({ en: 'Open CXone live queue', ar: 'فتح قائمة CXone المباشرة' })}
        </GlassButton>
      </div>
    </section>
  );
}
